import React, { useState, useEffect } from "react";
import { useRecipeApi } from "../../api/RecipeApi";
import IngredientSelector from "./IngredientSelector";
import AllergenSelector from "./AllergenSelector";
import CategorySelector from "./CategorySelector";
import type {
  Recipe,
  IngredientInput,
  AllergenInput,
  CategoryInput,
} from "../../types/Recipe";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { handleApiError } from "../../utils/handleApiError";
import { createPortal } from "react-dom";
import { useConfirm } from "../../context/ConfirmContext ";

interface RecipeFormProps {
  isModal?: boolean;
  isOpen?: boolean;
  onClose?: () => void;
  onSuccess?: (recipe: Recipe) => void;
  existingRecipe?: Recipe; // For edit mode
  mode?: "create" | "edit"; // 'create' or 'edit'
  onRecipeUpdate?: (updatedRecipe: Recipe) => void;
}

const RecipeForm: React.FC<RecipeFormProps> = ({
  isModal = false,
  isOpen = true,
  onClose,
  onSuccess,
  existingRecipe,
  mode = "create",
  onRecipeUpdate,
}) => {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");
  const [servings, setServings] = useState(1);
  const [prepTimeMinutes, setPrepTimeMinutes] = useState(0);
  const alertDialog = useConfirm();
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const navigate = useNavigate();
  const [ingredients, setIngredients] = useState<IngredientInput[]>([]);
  const [categories, setCategories] = useState<CategoryInput[]>([]);
  const [allergens, setAllergens] = useState<AllergenInput[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { createRecipe, updateRecipeData } = useRecipeApi();

  // Initialize form with existing recipe data for edit mode
  useEffect(() => {
    if (existingRecipe && mode === "edit") {
      setTitle(existingRecipe.title);
      setDescription(existingRecipe.description || "");
      setInstructions(existingRecipe.instructions || "");
      setServings(existingRecipe.servings);
      setPrepTimeMinutes(existingRecipe.prepTimeMinutes);
      setImagePreview(existingRecipe.imageUrl || null);

      // Set ingredients, categories, allergens from existing recipe
      if (existingRecipe.ingredients) {
        setIngredients(
          existingRecipe.ingredients.map((ri) => ({
            id: ri.id,
            name: ri.name,
            quantity: ri.quantity,
            unit: ri.unit || "",
          })),
        );
      }

      if (existingRecipe.categories) {
        setCategories(
          existingRecipe.categories.map((rc) => ({
            id: rc.id,
            name: rc.name,
          })),
        );
      }

      if (existingRecipe.allergens) {
        setAllergens(
          existingRecipe.allergens.map((ra) => ({
            id: ra.id,
            name: ra.name,
          })),
        );
      }
    }
  }, [existingRecipe, mode]);

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      await alertDialog({
        title: "For stor bildefil",
        message: `(Filen må være et bilde (JPG, PNG, etc).`,
      });
      e.target.value = "";
      return;
    }

    const maxSizeMB = 10;
    const sizeMB = file.size / 1024 / 1024;

    if (sizeMB > maxSizeMB) {
      await alertDialog({
        title: "For stor bildefil",
        message: `Bildefilen er for stor (${sizeMB.toFixed(2)} MB).\nMaks størrelse er ${maxSizeMB} MB.`,
      });
      e.target.value = ""; // reset file input
      return;
    }

    setImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      // Convert lists to JSON strings
      formData.append(
        "RecipeIngredients",
        JSON.stringify(
          ingredients.map((i) => ({
            ingredientId: i.id,
            quantity: i.quantity,
            unit: i.unit ?? null,
          })),
        ),
      );

      formData.append(
        "RecipeAllergens",
        JSON.stringify(
          allergens.map((a) => ({
            allergenId: a.id,
          })),
        ),
      );

      formData.append(
        "RecipeCategories",
        JSON.stringify(
          categories.map((c) => ({
            categoryId: c.id,
          })),
        ),
      );

      // Add simple values
      if (title !== existingRecipe?.title) formData.append("Title", title);
      if (description !== existingRecipe?.description)
        formData.append("Description", description);
      if (instructions !== existingRecipe?.instructions)
        formData.append("Instructions", instructions);
      if (servings !== existingRecipe?.servings)
        formData.append("Servings", servings.toString());
      if (prepTimeMinutes !== existingRecipe?.prepTimeMinutes)
        formData.append("PrepTimeMinutes", prepTimeMinutes.toString());

      // Add file
      if (imageFile) {
        formData.append("ImageFile", imageFile);
      }

      let response;
      if (mode === "create") {
        response = await createRecipe(formData);
      } else if (mode === "edit" && existingRecipe) {
        response = await updateRecipeData(existingRecipe.id, formData);
        if (onRecipeUpdate) {
          onRecipeUpdate(response.data.data as Recipe);
        }
      }

      if (response) {
        const result: Recipe = response.data.data;
        const successMessage =
          mode === "create"
            ? `Oppskrift "${result.title}" lagret!`
            : `Oppskrift "${result.title}" oppdatert!`;

        toast.success(successMessage);

        // Call success callback if provided
        if (onSuccess) {
          onSuccess(result);
        }

        // Reset form for create mode
        if (mode === "create") {
          setTitle("");
          setDescription("");
          setInstructions("");
          setServings(1);
          setPrepTimeMinutes(0);
          setImageFile(null);
          setImagePreview(null);
          setIngredients([]);
          setAllergens([]);
          setCategories([]);
        }

        // Close modal or navigate
        if (isModal && onClose) {
          onClose();
        } else if (mode === "create") {
          navigate(-1);
        }
      }
    } catch (err: any) {
      handleApiError(err, "Feil ved lagring av oppskrift");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const formContent = (
    <form
      onSubmit={handleSubmit}
      className="bg-white/90 backdrop-blur-md p-8 rounded-2xl shadow-lg flex flex-col gap-4"
    >
      <h1 className="text-2xl font-bold text-orange-600 mb-4 text-center">
        {mode === "create"
          ? "➕ Legg til ny oppskrift"
          : "✏️ Rediger oppskrift"}
      </h1>

      {/* Title */}
      <div>
        <p className="text-gray-500 text-sm mt-1">Navnet på oppskriften:</p>
        <input
          type="text"
          placeholder="Tittel på oppskrift"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-orange-400 outline-none"
          required
        />
      </div>

      {/* Description */}
      <div>
        <p className="text-gray-500 text-sm mt-1">
          Kort beskrivelse av retten:
        </p>
        <textarea
          placeholder="Beskrivelse..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-orange-400 outline-none resize-none"
          required
        />
      </div>

      {/* Instructions */}
      <div>
        <p className="text-gray-500 text-sm mt-1">
          Trinn-for-trinn fremgangsmåte:
        </p>
        <textarea
          placeholder="Instruksjoner..."
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={4}
          className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-orange-400 outline-none resize-none"
        />
      </div>

      {/* Servings & Prep Time */}
      <div className="flex gap-3">
        <div className="flex-1">
          <input
            type="number"
            min={1}
            max={100}
            value={servings}
            onChange={(e) => setServings(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-orange-400 outline-none"
            placeholder="Porsjoner"
          />
          <p className="text-gray-500 text-sm mt-1">
            Hvor mange personer oppskriften er til.
          </p>
        </div>
        <div className="flex-1">
          <input
            type="number"
            min={0}
            max={600}
            value={prepTimeMinutes}
            onChange={(e) => setPrepTimeMinutes(Number(e.target.value))}
            className="border border-gray-300 rounded-lg px-3 py-2 w-full focus:ring-2 focus:ring-orange-400 outline-none"
            placeholder="Tid (min)"
          />
          <p className="text-gray-500 text-sm mt-1">
            Forberedelsestid i minutter.
          </p>
        </div>
      </div>

      {/* Ingredients */}
      <IngredientSelector
        ingredients={ingredients}
        setIngredients={setIngredients}
      />
      <p className="text-gray-500 text-sm mt-1">
        Legg til ingredienser med mengde og enhet.
      </p>

      {/* Allergens */}
      <AllergenSelector allergens={allergens} setAllergens={setAllergens} />
      <p className="text-gray-500 text-sm mt-1">
        Velg allergener som retten inneholder.
      </p>

      {/* Categories */}
      <CategorySelector categories={categories} setCategories={setCategories} />
      <p className="text-gray-500 text-sm mt-1">
        Velg kategorier for oppskriften.
      </p>

      {/* Image Upload */}
      <label className="block mt-2">
        <span className="text-gray-700 font-medium">Last opp bilde</span>
        <input
          type="file"
          accept="image/*"
          onChange={handleImageChange}
          className="mt-2 block w-full text-sm text-gray-700
            file:mr-4 file:py-2 file:px-4
            file:rounded-lg file:border-0
            file:text-sm file:font-semibold
            file:bg-orange-100 file:text-orange-600
            hover:file:bg-orange-200"
        />
      </label>
      {imagePreview && (
        <img
          src={imagePreview}
          alt="Preview"
          className="rounded-lg shadow-md w-full object-cover max-h-64 mt-2"
        />
      )}

      {/* Buttons */}
      <div className="flex gap-3 mt-3">
        <button
          type="button"
          onClick={handleCancel}
          className="flex-1 bg-gray-500 text-white font-semibold py-2 rounded-lg hover:bg-gray-600 transition"
          disabled={isSubmitting}
        >
          Avbryt
        </button>
        <button
          type="submit"
          className="flex-1 bg-orange-500 text-white font-semibold py-2 rounded-lg hover:bg-orange-600 transition disabled:opacity-50"
          disabled={isSubmitting}
        >
          {isSubmitting
            ? "Lagrer..."
            : mode === "create"
              ? "Lagre oppskrift"
              : "Oppdater oppskrift"}
        </button>
      </div>
    </form>
  );

  // Modal version
  if (isModal) {
    if (!isOpen) return null;

    return createPortal(
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[99999]">
        <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="p-6">{formContent}</div>
        </div>
      </div>,
      document.body,
    );
  }

  // Page version
  return (
    <div className="min-h-screen bg-orange-50 pt-20 pb-12 px-3 sm:px-6">
      <div className="w-full max-w-lg mx-auto">{formContent}</div>
    </div>
  );
};

export default RecipeForm;
